import { px } from "./utils";

/* ─── Phase 3 demo data: complaints ──────────────────────────
   Safety ratings no longer has a demo table — see Known Issue #1's
   fix below; a silent fallback to two hardcoded vehicles was the
   actual bug, not a feature worth keeping a data table around for. */
export const DEMO_COMPLAINTS={
  "Chevrolet|Tahoe|2021":[
    {id:"c1",component:"ELECTRICAL SYSTEM",summary:"Infotainment system randomly reboots while driving at highway speed.",dateAdded:"2022-03-15",crash:false,fire:false,injuries:0},
    {id:"c2",component:"SUSPENSION",summary:"Front suspension clunking noise over bumps at low speeds.",dateAdded:"2022-07-22",crash:false,fire:false,injuries:0},
  ],
  "Ford|F-150|2020":[
    {id:"c3",component:"ENGINE AND ENGINE COOLING",summary:"Engine hesitation and rough idle at low RPM under light load conditions.",dateAdded:"2021-05-10",crash:false,fire:false,injuries:0},
    {id:"c4",component:"POWER TRAIN",summary:"10-speed transmission hunting between gears in stop-and-go traffic.",dateAdded:"2021-09-14",crash:false,fire:false,injuries:0},
    {id:"c5",component:"ELECTRICAL SYSTEM",summary:"SYNC 4 system freezes requiring battery disconnect to reset.",dateAdded:"2022-01-03",crash:false,fire:false,injuries:0},
  ],
  "Ford|Mustang|1965":[],
};
export function getDemoComplaints(make,model,year){return DEMO_COMPLAINTS[`${make}|${model}|${year}`]??[];}

/* ─── NHTSA Safety Ratings — Known Issue #1 fix ──────────────────
   A live-call failure and "NHTSA genuinely has nothing for this
   vehicle" used to collapse to the same bare null, and the old demo-
   table fallback made it worse: it silently swapped in real-looking
   data for exactly the two vehicles that table happened to cover,
   so a failure looked identical to genuine live data for those two
   cars specifically. Now: throws on a network/API failure (caught
   and re-thrown with a clean message, same convention as
   fetchComplaintsLive below), and only returns null when NHTSA
   actually responded and had nothing — two distinguishable outcomes,
   no silent substitution either way. */
export async function fetchSafetyRatings(year,make,model){
  try{
    const listRes=await fetch(px(`https://api.nhtsa.gov/SafetyRatings/modelyear/${year}/make/${encodeURIComponent(make)}/model/${encodeURIComponent(model)}`));
    if(!listRes.ok)throw new Error("NHTSA unavailable");
    const listJson=await listRes.json();
    const vehicleId=listJson.Results?.[0]?.VehicleId;
    if(!vehicleId)return null; // NHTSA responded — genuinely no match for this vehicle

    const ratingRes=await fetch(px(`https://api.nhtsa.gov/SafetyRatings/VehicleId/${vehicleId}`));
    if(!ratingRes.ok)throw new Error("NHTSA unavailable");
    const r=(await ratingRes.json()).Results?.[0];
    if(!r)return null; // NHTSA responded — vehicle exists but no rating on file

    return{overall:r.OverallRating,frontal:r.OverallFrontCrashRating,side:r.OverallSideCrashRating,rollover:r.RolloverRating,rolloverRisk:r.RolloverPossibility?`${(r.RolloverPossibility*100).toFixed(1)}%`:null,source:"live"};
  }catch(e){
    throw new Error(e.message||"Could not load safety ratings");
  }
}

/* ─── Phase 3: NHTSA Complaints ──────────────────────────────── */
export async function fetchComplaintsLive(make,model,year){
  try{
    const res=await fetch(px(`https://api.nhtsa.gov/complaints/complaintsByVehicle?make=${encodeURIComponent(make)}&model=${encodeURIComponent(model)}&modelYear=${year}`));
    if(!res.ok)throw new Error("NHTSA unavailable");
    const json=await res.json();
    return(json.results||[]).slice(0,15).map((c,i)=>({id:`live_${i}`,component:c.components,summary:c.summary,dateAdded:c.dateAdded||c.dateComplaintFiled||"",crash:c.crash||false,fire:c.fire||false,injuries:c.numberOfInjuries||0,deaths:c.numberOfDeaths||0}));
  }catch(e){throw new Error(e.message||"Could not load complaints");}
}
export async function fetchComplaints(make,model,year){
  try{
    const items=await fetchComplaintsLive(make,model,year);
    return{items,source:"live"};
  }catch{
    return{items:getDemoComplaints(make,model,year),source:"demo"};
  }
}

/* ─── Priority 4c: complaint-trend summary ────────────────────────
   Splits a raw NHTSA "components" string into individual category
   names. NHTSA joins multiple categories with a bare comma (no
   following space) but at least one official category name itself
   contains a comma WITH a following space ("FUEL SYSTEM, GASOLINE") —
   confirmed against NHTSA's own 20-category component list, not
   assumed. Splitting only on a comma NOT followed by whitespace
   handles both correctly, tested against every multi-value example
   pulled from a live fetch during investigation. Inferred from
   observed data, not a documented NHTSA parsing spec — low stakes if
   occasionally wrong on some future edge case, since this only feeds
   a "most reported" summary, nothing safety/compliance-critical. */
export function splitComponents(raw){
  if(!raw)return[];
  return String(raw).split(/,(?!\s)/).map(s=>s.trim()).filter(Boolean);
}
